"use client";

import { useEffect, useState } from "react";

const CONFERENCES_URL =
  "https://eventscons.com/eventbackendnew/api/conferences/all";
const BOOKS_BASE_URL =
  "https://eventscons.com/eventbackendnew/api/conference-book";

function getAuthToken() {
  if (typeof window === "undefined") return null;
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("access_token");

  console.log("Auth token from localStorage =>", token);
  return token;
}

export default function ConferencesBook() {
  const [conferences, setConferences] = useState([]);
  const [selectedConference, setSelectedConference] = useState("");
  const [books, setBooks] = useState([]);
  const [loadingConferences, setLoadingConferences] = useState(false);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [title, setTitle] = useState("");

  // Conferences fetch
  const fetchConferences = async () => {
    try {
      setLoadingConferences(true);
      setError(null);
      const token = getAuthToken();

      const res = await fetch(CONFERENCES_URL, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) {
        throw new Error("Failed to load conferences");
      }

      const json = await res.json();
      const list = json.data || [];
      setConferences(list);

      if (list.length > 0 && !selectedConference) {
        setSelectedConference(String(list[0].id));
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoadingConferences(false);
    }
  };

  // Books fetch by conference
  const fetchBooks = async (conferenceId) => {
    const confId = conferenceId || selectedConference;
    if (!confId) {
      setBooks([]);
      return;
    }

    try {
      setLoadingBooks(true);
      setError(null);
      const token = getAuthToken();
      if (!token) throw new Error("Authentication token not found");

      const res = await fetch(`${BOOKS_BASE_URL}/${confId}/all`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to load books");
      }

      const json = await res.json();
      setBooks(json.data || []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoadingBooks(false);
    }
  };

  // Add / Update book (conference_id, title, pdf_file)
  const handleSubmitBook = async (e) => {
    e.preventDefault();
    if (!selectedConference) {
      alert("Please select a conference first");
      return;
    }
    if (!title.trim()) {
      alert("Title is required");
      return;
    }
    if (!pdfFile && !editingBook) {
      alert("Please select a PDF file");
      return;
    }

    try {
      setError(null);
      const token = getAuthToken();
      if (!token) throw new Error("Authentication token not found");

      const formData = new FormData();
      formData.append("conference_id", selectedConference);
      formData.append("title", title);
      if (pdfFile) {
        // backend requirement: pdf_file
        formData.append("pdf_file", pdfFile);
      }

      const isEdit = !!editingBook;
      const url = isEdit
        ? `${BOOKS_BASE_URL}/${editingBook.id}/update`
        : `${BOOKS_BASE_URL}/store`;

      console.log("Submitting to:", url);
      for (const pair of formData.entries()) {
        console.log("FORMDATA:", pair[0], pair[1]);
      }

      const res = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          // Content-Type not set for FormData [web:70][web:55]
        },
        body: formData,
      });

      const text = await res.text();
      console.log("Add/Update book status:", res.status, "Body:", text);

      if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
      }

      setShowForm(false);
      setEditingBook(null);
      setPdfFile(null);
      setTitle("");
      fetchBooks();
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
    }
  };

  // Delete book
  const handleDeleteBook = async (bookId) => {
    if (!window.confirm("Are you sure you want to delete this book?")) return;

    try {
      setError(null);
      const token = getAuthToken();
      if (!token) throw new Error("Authentication token not found");

      const url = `${BOOKS_BASE_URL}/${bookId}/delete`;
      console.log("Delete URL:", url);

      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const text = await res.text();
      console.log("Delete status:", res.status, "Body:", text);

      if (!res.ok) {
        throw new Error("Failed to delete book");
      }

      setBooks((prev) => prev.filter((b) => b.id !== bookId));
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
    }
  };

  const openAddForm = () => {
    setEditingBook(null);
    setPdfFile(null);
    setTitle("");
    setShowForm(true);
  };

  const openEditForm = (book) => {
    setEditingBook(book);
    setPdfFile(null);
    setTitle(book.title || "");
    setShowForm(true);
  };

  useEffect(() => {
    fetchConferences();
  }, []);

  useEffect(() => {
    if (selectedConference) {
      fetchBooks(selectedConference);
    }
  }, [selectedConference]);

  return (
    <div
      style={{
        padding: "24px",
        maxWidth: "1200px",
        width:'100%',
        margin: "0 auto",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      <h1 style={{ fontSize: "32px", fontWeight: 700, marginBottom: 16 }}>
        Conferences Book
      </h1>

      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 8,
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <select
          value={selectedConference}
          onChange={(e) => setSelectedConference(e.target.value)}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #cbd5f5",
            minWidth: 220,
            fontSize: 14,
          }}
        >
          <option value="">
            {loadingConferences ? "Loading conferences..." : "Select Conference"}
          </option>
          {conferences.map((conf) => (
            <option key={conf.id} value={conf.id}>
              {conf.title || conf.name || `Conference ${conf.id}`}
            </option>
          ))}
        </select>

        <button
          onClick={() => fetchBooks()}
          disabled={!selectedConference}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            backgroundColor: "#9B1321",
            color: "white",
            fontWeight: 500,
            cursor: selectedConference ? "pointer" : "not-allowed",
          }}
        >
          Refresh Books
        </button>

        <button
          onClick={openAddForm}
          disabled={!selectedConference}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            backgroundColor: "#16a34a",
            color: "white",
            fontWeight: 500,
            cursor: selectedConference ? "pointer" : "not-allowed",
          }}
        >
          Add Book
        </button>
      </div>

      {loadingBooks ? (
        <p>Loading books...</p>
      ) : books.length === 0 ? (
        <p>
          {selectedConference
            ? "No books found for this conference."
            : "Please select a conference to view books."}
        </p>
      ) : (
        <div
          style={{
            backgroundColor: "white",
            borderRadius: 12,
            boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
            overflowX: "auto",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 14,
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    padding: "12px 16px",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  ID
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "12px 16px",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  Title
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "12px 16px",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  PDF
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "12px 16px",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {books.map((book) => (
                <tr key={book.id}>
                  <td
                    style={{
                      padding: "10px 16px",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    {book.id}
                  </td>
                  <td
                    style={{
                      padding: "10px 16px",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    {book.title || "-"}
                  </td>
                  <td
                    style={{
                      padding: "10px 16px",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    {book.pdf_url || book.pdf_file || book.file || "PDF"}
                  </td>
                  <td
                    style={{
                      padding: "10px 16px",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <button
                      onClick={() => openEditForm(book)}
                      style={{
                        padding: "6px 10px",
                        marginRight: 8,
                        borderRadius: 6,
                        border: "none",
                        backgroundColor: "#2563eb",
                        color: "white",
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteBook(book.id)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 6,
                        border: "none",
                        backgroundColor: "#dc2626",
                        color: "white",
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div
          style={{
            marginTop: 24,
            padding: 16,
            borderRadius: 12,
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
          }}
        >
          <h3 style={{ marginBottom: 12, fontSize: 18, fontWeight: 600 }}>
            {editingBook ? "Edit Book" : "Add Book"}
          </h3>
          <form onSubmit={handleSubmitBook}>
            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontSize: 14,
                }}
              >
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  fontSize: 14,
                }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontSize: 14,
                }}
              >
                PDF File
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) =>
                  setPdfFile(
                    e.target.files && e.target.files[0]
                      ? e.target.files[0]
                      : null
                  )
                }
              />
              {editingBook && !pdfFile && (
                <p style={{ fontSize: 12, color: "#6b7280" }}>
                  Leave empty to keep existing PDF.
                </p>
              )}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="submit"
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: "#16a34a",
                  color: "white",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {editingBook ? "Update" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingBook(null);
                  setPdfFile(null);
                  setTitle("");
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  backgroundColor: "white",
                  color: "#374151",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}